<template>
  <WidgetCadre
    titre="Mes tâches"
    v-bind="apparence"
    :lien="donnees?.lien || '/projets/mes-taches'"
    :libelle-lien="`Tout voir${donnees?.total ? ' (' + donnees.total + ')' : ''}`"
    :chargement="chargement"
    :panne="panne"
    :reglable="reglable"
    @retirer="$emit('retirer')"
    @recharger="$emit('recharger')"
  >
    <template #indice>
      <span
        v-if="donnees?.en_retard"
        class="mes-taches__retard"
      >{{ donnees.en_retard }} en retard</span>
    </template>

    <div class="mes-taches">
      <button
        v-for="ligne in lignes"
        :key="cleLigne(ligne)"
        class="mini surface"
        :class="{ 'mini--retard': ligne.en_retard }"
        type="button"
        @click="ouvrir(ligne)"
      >
        <Icon
          v-if="estEtape(ligne)"
          class="mini__marque"
          icon="mdi:sitemap-outline"
          :width="13"
          title="Étape de processus"
        />
        <span
          v-else
          class="mini__pastille"
          :style="{ backgroundColor: couleurLigne(ligne) }"
        />
        <span class="mini__titre">{{ ligne.titre }}</span>
        <span class="mini__projet">{{ provenance(ligne) }}</span>
        <span class="mini__echeance">{{ ligne.echeance ? dateCourte(ligne.echeance) : '' }}</span>
      </button>

      <p
        v-if="!lignes.length"
        class="mes-taches__vide"
      >
        Rien qui vous soit assigné pour l'instant.
      </p>
    </div>
  </WidgetCadre>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { Icon } from '@iconify/vue'
import { cleLigne, couleurLigne, destination, estEtape, provenance } from '@/utils/mesTaches'
import WidgetCadre from './WidgetCadre.vue'
import { apparenceDe } from './apparence'

/**
 * Les lignes les plus pressantes, tâches de kanban et étapes de processus
 * mêlées. Le tri, le plafond et les compteurs viennent du serveur : les
 * chiffres portent sur le périmètre entier, pas sur l'extrait affiché.
 *
 * Le rendu d'une ligne passe par `utils/mesTaches` — le même que l'écran
 * complet et que l'ancien encart, pour qu'une tâche ne se dessine pas de deux
 * façons selon l'endroit où on la regarde.
 */

const props = defineProps({
    donnees: { type: Object, default: null },
    chargement: { type: Boolean, default: false },
    panne: { type: Boolean, default: false },
    reglable: { type: Boolean, default: false },
})

defineEmits(['retirer', 'recharger'])

const apparence = apparenceDe('mes-taches')

const router = useRouter()

const lignes = computed(() => props.donnees?.lignes ?? [])

function ouvrir(ligne) {
    const cible = destination(ligne)

    if (cible) router.push(cible)
}

function dateCourte(iso) {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}
</script>

<style lang="scss" scoped>
.mes-taches {
  display: flex;
  flex-direction: column;
  gap: 0;

  &__retard {
    font-size: 11.5px;
    font-weight: 600;
    color: var(--el-color-danger);
    background: var(--el-color-danger-light-9);
    border-radius: 6px;
    padding: 2px 8px;
  }

  &__vide {
    margin: 4px 0;
    font-size: 12.5px;
    color: var(--el-text-color-secondary);
  }
}

.mini {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  cursor: pointer;
  text-align: left;


  /* Un filet entre les lignes plutôt qu'un aplat par ligne : la liste se lit
     d'un trait, et le survol seul désigne ce qui est cliquable. En bordure et
     non en ombre portée : l'ombre servirait le même canal que le repère « en
     retard », et l'effacerait sur toute ligne qui en suit une autre. */
  & + & { border-top: 1px solid var(--el-border-color-lighter); }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  &--retard { box-shadow: inset 3px 0 0 var(--el-color-danger); }

  &__marque {
    /** Le ton des processus, aligné sur `utils/mesTaches.js`. */
    color: #7d5bd6;
    flex-shrink: 0;
  }

  &__pastille {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__titre {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: var(--el-text-color-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__projet {
    font-size: 11.5px;
    color: var(--el-text-color-secondary);
    flex-shrink: 0;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__echeance {
    font-size: 11.5px;
    color: var(--el-text-color-secondary);
    min-width: 52px;
    text-align: right;
    flex-shrink: 0;
  }
}
</style>
